import React, { useRef, useState, useCallback, useEffect, useImperativeHandle, forwardRef } from 'react';
import './OptionWheel.css';

const DEFAULT_ITEMS = [
  'Bollywood',
  'Lo-Fi',
  'Pop',
  'Hip-Hop',
  'Acoustic',
  'Romantic',
  'EDM',
  'Rock',
  'Punjabi',
  'Indie',
  'Retro',
  'Sad Vibes'
];

const OptionWheel = forwardRef(({
  items = DEFAULT_ITEMS,
  defaultSelected = 2,
  onChange,
  onItemSelect,
  textColor = '#888888',
  activeColor = '#00ffff',
  side = 'left',
  fontSize = 2.2,
  spacing = 1.35,
  curve = 1,
  tilt = 6,
  blur = 2,
  fade = 0.28,
  minOpacity = 0.05,
  smoothing = 200,
  inset = 24,
  loop = true,
  draggable = true,
  soundUrl = '',
  soundVolume = 0.5,
  showLever = true,
  className = ''
}, ref) => {
  const rootRef = useRef(null);
  const itemRefs = useRef([]);
  const posRef = useRef(defaultSelected);
  const targetRef = useRef(defaultSelected);
  const rafRef = useRef(null);
  const lastRef = useRef(0);
  const cfgRef = useRef({});
  const onChangeRef = useRef(onChange);
  const onItemSelectRef = useRef(onItemSelect);
  const selectedRef = useRef(defaultSelected);
  const wheelTimerRef = useRef(null);
  const dragRef = useRef(null);
  const dragMovedRef = useRef(false);
  const audioRef = useRef(null);
  const audioUrlRef = useRef('');
  const lastTickRef = useRef(0);
  
  const [selectedIndex, setSelectedIndex] = useState(defaultSelected);
  const [isDragging, setIsDragging] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const [leverPulled, setLeverPulled] = useState(false);

  const remPx = typeof window !== 'undefined' ? parseFloat(getComputedStyle(document.documentElement).fontSize) || 16 : 16;

  onChangeRef.current = onChange;
  onItemSelectRef.current = onItemSelect;

  cfgRef.current = {
    count: items.length,
    items,
    rowH: Math.max(fontSize * spacing * remPx, 1),
    curve,
    tilt,
    blur,
    fade,
    minOpacity,
    side,
    loop,
    smoothing,
    draggable,
    soundUrl,
    soundVolume
  };

  const runFrame = useCallback(now => {
    const dt = Math.min((now - lastRef.current) / 1000, 0.05);
    lastRef.current = now;
    const cfg = cfgRef.current;
    const tau = Math.max(cfg.smoothing, 1) / 1000;
    const k = 1 - Math.exp(-dt / tau);

    const target = targetRef.current;
    const cur = posRef.current;
    let next = cur + (target - cur) * k;
    const settled = Math.abs(target - next) < 0.001;
    if (settled) next = target;
    posRef.current = next;

    const els = itemRefs.current;
    const n = cfg.count;
    const mirror = cfg.side === 'right' ? -1 : 1;
    const tiltRad = (cfg.tilt * Math.PI) / 180;
    const R = tiltRad > 0.0005 ? cfg.rowH / tiltRad : 0;
    for (let i = 0; i < n; i++) {
      const el = els[i];
      if (!el) continue;
      let d = i - next;
      if (cfg.loop && n > 1) {
        d = ((d % n) + n) % n;
        if (d > n / 2) d -= n;
      }
      const dist = Math.abs(d);
      let x = 0;
      let y = d * cfg.rowH;
      let rot = 0;
      if (R > 0) {
        const ang = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, d * tiltRad));
        y = R * Math.sin(ang);
        x = -mirror * R * (1 - Math.cos(ang)) * cfg.curve;
        rot = (mirror * ang * 180) / Math.PI;
      }
      el.style.transform = `translate(${x.toFixed(2)}px, calc(${y.toFixed(2)}px - 50%)) rotate(${rot.toFixed(3)}deg)`;
      el.style.opacity = String(Math.max(cfg.minOpacity, 1 - dist * cfg.fade));
      el.style.filter = cfg.blur > 0 ? `blur(${(dist * cfg.blur).toFixed(2)}px)` : 'none';
      el.style.setProperty('--ow-p', Math.max(0, 1 - Math.min(dist, 1)).toFixed(4));
    }

    rafRef.current = settled ? null : requestAnimationFrame(runFrame);
  }, []);

  const startLoop = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
    }
    lastRef.current = performance.now();
    rafRef.current = requestAnimationFrame(runFrame);
  }, [runFrame]);

  const playTick = useCallback(() => {
    const { soundUrl, soundVolume } = cfgRef.current;
    if (!soundUrl) return;
    const now = performance.now();
    if (now - lastTickRef.current < 70) return;
    lastTickRef.current = now;
    if (!audioRef.current || audioUrlRef.current !== soundUrl) {
      audioRef.current = new Audio(soundUrl);
      audioRef.current.preload = 'auto';
      audioUrlRef.current = soundUrl;
    }
    const audio = audioRef.current;
    audio.volume = Math.min(Math.max(soundVolume, 0), 1);
    audio.currentTime = 0;
    audio.play()?.catch(() => {});
  }, []);

  const applyTarget = useCallback(
    (value, snap) => {
      const cfg = cfgRef.current;
      let v = value;
      if (!cfg.loop) v = Math.min(Math.max(v, 0), Math.max(cfg.count - 1, 0));
      if (snap) v = Math.round(v);
      targetRef.current = v;
      const idx = ((Math.round(v) % cfg.count) + cfg.count) % cfg.count;
      if (idx !== selectedRef.current) {
        selectedRef.current = idx;
        setSelectedIndex(idx);
        onChangeRef.current?.(idx, cfg.items[idx]);
        playTick();
      }
      startLoop();
    },
    [startLoop, playTick]
  );

  // --- ROULETTE LEVER SPIN ACTION ---
  const spinRoulette = useCallback(() => {
    if (isSpinning) return;
    setIsSpinning(true);
    setLeverPulled(true);

    // Click feedback
    if (navigator.vibrate) navigator.vibrate(50);

    const cfg = cfgRef.current;
    const cur = targetRef.current;
    
    // Choose a random distance: 2 to 4 full rotations + random index offset
    const randomLaps = 2 + Math.floor(Math.random() * 3);
    const randomIndex = Math.floor(Math.random() * cfg.count);
    const totalSteps = randomLaps * cfg.count + randomIndex;
    
    // Animate smoothly to high velocity then ease down like a casino roulette wheel
    const finalTarget = Math.round(cur + totalSteps);
    
    // Slightly lengthen smoothing during roulette spin for cinematic decelerating feel
    const prevSmoothing = cfg.smoothing;
    cfg.smoothing = 450;
    
    applyTarget(finalTarget, true);

    setTimeout(() => {
      setLeverPulled(false);
    }, 400);

    // After deceleration settles, finalize selection and trigger action
    setTimeout(() => {
      cfg.smoothing = prevSmoothing;
      setIsSpinning(false);
      const chosenIdx = ((Math.round(finalTarget) % cfg.count) + cfg.count) % cfg.count;
      if (onItemSelectRef.current) {
        onItemSelectRef.current(cfg.items[chosenIdx], chosenIdx);
      }
    }, 1800);
  }, [isSpinning, applyTarget]);

  useImperativeHandle(ref, () => ({
    spin: spinRoulette
  }));

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const onWheel = e => {
      if (isSpinning) return;
      e.preventDefault();
      const cfg = cfgRef.current;
      const delta = e.deltaMode === 1 ? e.deltaY * 24 : e.deltaY;
      const step = Math.max(-1, Math.min(1, delta / cfg.rowH));
      applyTarget(targetRef.current + step, false);
      if (wheelTimerRef.current) clearTimeout(wheelTimerRef.current);
      wheelTimerRef.current = setTimeout(() => applyTarget(targetRef.current, true), 140);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      if (wheelTimerRef.current) clearTimeout(wheelTimerRef.current);
    };
  }, [applyTarget, isSpinning]);

  const handlePointerDown = useCallback(e => {
    if (!cfgRef.current.draggable || isSpinning) return;
    dragRef.current = { y: e.clientY, start: targetRef.current, id: e.pointerId };
    dragMovedRef.current = false;
    setIsDragging(true);
  }, [isSpinning]);

  const handlePointerMove = useCallback(
    e => {
      const drag = dragRef.current;
      if (!drag) return;
      const dy = e.clientY - drag.y;
      if (!dragMovedRef.current && Math.abs(dy) > 4) {
        dragMovedRef.current = true;
        rootRef.current?.setPointerCapture(drag.id);
      }
      if (dragMovedRef.current) applyTarget(drag.start - dy / cfgRef.current.rowH, false);
    },
    [applyTarget]
  );

  const handlePointerEnd = useCallback(() => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setIsDragging(false);
    if (dragMovedRef.current) applyTarget(targetRef.current, true);
  }, [applyTarget]);

  const handleItemClick = useCallback(
    index => {
      if (dragMovedRef.current || isSpinning) return;
      const cfg = cfgRef.current;
      const cur = targetRef.current;
      let d = index - (((cur % cfg.count) + cfg.count) % cfg.count);
      if (cfg.loop && cfg.count > 1) {
        if (d > cfg.count / 2) d -= cfg.count;
        else if (d < -cfg.count / 2) d += cfg.count;
      }
      applyTarget(cur + d, true);
      onItemSelectRef.current?.(cfg.items[index], index);
    },
    [applyTarget, isSpinning]
  );

  const handleKeyDown = useCallback(
    e => {
      if (isSpinning) return;
      let delta = null;
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') delta = -1;
      else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') delta = 1;
      if (delta == null) return;
      e.preventDefault();
      applyTarget(Math.round(targetRef.current) + delta, true);
    },
    [applyTarget, isSpinning]
  );

  useEffect(() => {
    applyTarget(targetRef.current, false);
  }, [items, fontSize, spacing, curve, tilt, blur, fade, minOpacity, side, loop, smoothing, applyTarget]);

  useEffect(
    () => () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      audioRef.current?.pause();
    },
    []
  );

  return (
    <div className="option-wheel-container">
      <div
        ref={rootRef}
        role="listbox"
        tabIndex={0}
        aria-label="Option wheel"
        className={`option-wheel${side === 'right' ? ' option-wheel--right' : ''}${isDragging ? ' option-wheel--dragging' : ''}${className ? ` ${className}` : ''}`}
        style={{
          '--ow-text-color': textColor,
          '--ow-active-color': activeColor,
          '--ow-font-size': `${fontSize}rem`,
          '--ow-inset': `${inset}px`
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onKeyDown={handleKeyDown}
      >
        {items.map((label, index) => (
          <div
            key={`${label}-${index}`}
            ref={el => {
              itemRefs.current[index] = el;
            }}
            role="option"
            aria-selected={selectedIndex === index}
            className={`option-wheel__item${selectedIndex === index ? ' option-wheel__item--selected' : ''}`}
            onClick={() => handleItemClick(index)}
          >
            {label}
          </div>
        ))}
      </div>

      {showLever && (
        <div className="wheel-lever-rig" onClick={spinRoulette} title="Pull Lever to Spin Genre Roulette!">
          <div className="wheel-lever-housing">
            <div className={`wheel-lever-arm ${leverPulled ? 'pulled' : ''}`}>
              <div className="wheel-lever-knob">
                <span className="knob-glow"></span>
              </div>
            </div>
            <div className="wheel-lever-base"></div>
          </div>
          <div className="wheel-lever-label">
            {isSpinning ? 'SPINNING...' : 'PULL TO SPIN'}
          </div>
        </div>
      )}
    </div>
  );
});

export default OptionWheel;
